/**
 * Utility class for map-related geometry calculations.
 * Requires @types/google.maps to be installed.
 */
export class MapUtils {
  /**
   * Calculates the distance between two points using the Haversine formula.
   * Returns distance in meters.
   */
  static haversineDistanceMeters(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const toRad = (v: number) => (v * Math.PI) / 180;
    const R = 6371000; // Earth's radius in meters
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Snaps a LatLng point to the nearest point on a polyline path.
   */
  static getSnappedPointToPolyline(
    position: google.maps.LatLng,
    path: google.maps.LatLng[]
  ): google.maps.LatLng {
    if (!path || path.length === 0) return position;

    let closestPoint = position;
    let minDistance = Number.MAX_VALUE;

    for (let i = 0; i < path.length - 1; i++) {
      const segmentStart = path[i];
      const segmentEnd = path[i + 1];

      const headingToPosition = google.maps.geometry.spherical.computeHeading(
        segmentStart,
        position
      );
      const headingToSegmentEnd = google.maps.geometry.spherical.computeHeading(
        segmentStart,
        segmentEnd
      );
      const segmentLength =
        google.maps.geometry.spherical.computeDistanceBetween(
          segmentStart,
          segmentEnd
        );
      const distanceToStart =
        google.maps.geometry.spherical.computeDistanceBetween(
          segmentStart,
          position
        );

      const projection =
        distanceToStart *
        Math.cos(((headingToPosition - headingToSegmentEnd) * Math.PI) / 180);

      const t = Math.max(0, Math.min(1, projection / segmentLength));
      const projected = google.maps.geometry.spherical.interpolate(
        segmentStart,
        segmentEnd,
        t
      );
      const distance = google.maps.geometry.spherical.computeDistanceBetween(
        position,
        projected
      );

      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = projected;
      }
    }

    return closestPoint;
  }

  /**
   * Helper to format LatLng to LatLngLiteral.
   */
  static toLiteral(latLng: google.maps.LatLng): google.maps.LatLngLiteral {
    return { lat: latLng.lat(), lng: latLng.lng() };
  }
}
